import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Globe, BookOpen } from 'lucide-react';
import CourseCard from '../components/CourseCard';
import { courses } from '../data/courses';
import copy from '../copy';

const Home: React.FC = () => {
  const featuredCourses = courses.slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div className="mb-8 md:mb-0">
            <h1 className="text-4xl font-bold text-primary-900 mb-4">
              {copy.homepage_bienvenida}
            </h1>
            <p className="text-lg text-primary-700 mb-6">
              {copy.homepage_subtitulo}
            </p>
            <p className="text-primary-600 mb-6">
              {copy.homepage_mision}
            </p>
            <Link to="/cursos" className="btn-primary">
              {copy.homepage_explorar}
            </Link>
          </div>
          <div className="relative">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-primary-100">
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 text-accent-600">50+</div>
                  <div className="text-primary-600 font-medium">Cursos Disponibles</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 text-accent-600">10K+</div>
                  <div className="text-primary-600 font-medium">Estudiantes</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 text-accent-600">4.8</div>
                  <div className="text-primary-600 font-medium">Calificación Promedio</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2 text-accent-600">24/7</div>
                  <div className="text-primary-600 font-medium">Soporte</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Nuestra Misión
            </h2>
            <p className="text-xl text-primary-600 max-w-4xl mx-auto leading-relaxed">
              Creemos que la educación tecnológica tiene el poder de transformar vidas y dar forma al futuro. Al nutrir el talento y fomentar la innovación, contribuimos al crecimiento de individuos, industrias y sociedades.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-100 to-tech-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-10 h-10 text-accent-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-4">
                Alianzas Universitarias
              </h3>
              <p className="text-primary-600 leading-relaxed">
                Establecemos alianzas estratégicas entre universidades líderes en Estados Unidos y universidades en la región iberoamericana. A través de estas colaboraciones, creamos vías para el intercambio de experiencia, desarrollo curricular y capacitación docente.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-100 to-tech-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <BookOpen className="w-10 h-10 text-accent-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-4">
                Desarrollo de Cursos
              </h3>
              <p className="text-primary-600 leading-relaxed">
                Nuestro equipo trabaja de la mano con miembros de la facultad para diseñar y adaptar cursos que se alineen con las demandas de la industria. Estos cursos cubren una amplia gama de áreas tecnológicas, desde inteligencia artificial y ciencia de datos hasta ciberseguridad y más.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-accent-100 to-tech-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <GraduationCap className="w-10 h-10 text-accent-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-4">
                Empoderamiento Docente
              </h3>
              <p className="text-primary-600 leading-relaxed">
                Empoderamos a los miembros de la facultad con el conocimiento y los recursos para impartir educación tecnológica de vanguardia. A través de sesiones de capacitación, talleres y colaboración con expertos, nos aseguramos de que los educadores estén bien preparados para guiar a los estudiantes en su viaje de aprendizaje.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section className="section-padding bg-primary-50">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Cursos Destacados
            </h2>
            <p className="text-xl text-primary-600 max-w-3xl mx-auto">
              Descubre nuestros cursos más populares y comienza tu viaje hacia el éxito profesional.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/cursos" className="btn-primary inline-flex items-center">
              Ver Todos los Cursos
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-800 to-primary-900 text-white">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Únete a nosotros en este viaje transformador.
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            En la Fundación Iberoamericana de Tecnología, estamos construyendo puentes de conocimiento y oportunidad, conectando a los estudiantes de hoy con las innovaciones del mañana.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cursos" className="btn-primary bg-white text-primary-900 hover:bg-primary-50 inline-flex items-center">
              {copy.homepage_explorar}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 